import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { resolveImageUrl } from "@/utils/imageUrl";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useSiteCustomization } from "@/context/SiteCustomizationContext";
import { NotificationBell } from "@/components/NotificationBell";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Search,
  ShoppingCart,
  User,
  Menu,
  ChevronDown,
  LogOut,
  LayoutDashboard,
  Settings,
  History,
  Monitor,
} from "lucide-react";

export default function Header() {
  const { user, token, logout, isAdmin } = useAuth();
  const { itemCount } = useCart();
  const { isSectionEnabled } = useSiteCustomization();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [adminDarkMode, setAdminDarkMode] = useState(false);
  const [deviceCount, setDeviceCount] = useState(null);

  const isHomePage = location.pathname === "/";
  const isAdminPage = location.pathname.startsWith("/admin");

  // Fetch device count
  useEffect(() => {
    if (!user || !token) { setDeviceCount(null); return; }
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/my-devices`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => setDeviceCount(res.data.length)).catch(() => {});
  }, [user, token]);

  // Sync with admin dark mode from localStorage
  useEffect(() => {
    if (isAdminPage) {
      const checkTheme = () => setAdminDarkMode(localStorage.getItem("admin-theme") === "dark");
      checkTheme();
      // Listen for storage changes (from admin toggle)
      window.addEventListener("storage", checkTheme);
      // Also poll for same-tab changes since storage event only fires cross-tab
      const interval = setInterval(checkTheme, 300);
      return () => {
        window.removeEventListener("storage", checkTheme);
        clearInterval(interval);
      };
    } else {
      setAdminDarkMode(false);
    }
  }, [isAdminPage]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isDark = isAdminPage && adminDarkMode;

  const headerBg = isDark
    ? "bg-gray-800 border-b border-gray-600/50"
    : isHomePage
      ? scrolled
        ? "bg-white/95 backdrop-blur-lg border-b border-gray-100 shadow-sm"
        : "bg-transparent"
      : "bg-white border-b border-gray-100";

  const textColor = isDark ? "text-gray-100" : isHomePage && !scrolled ? "text-white" : "text-navy-900";
  const hoverBg = isDark ? "hover:bg-gray-800" : isHomePage && !scrolled ? "hover:bg-white/10" : "hover:bg-gray-100";

  const activeColor = "#e0482d";
  const isActive = (path) => location.pathname.startsWith(path);
  const navLinkClass = (path) => {
    if (isActive(path)) return `font-medium transition-colors duration-300 rounded-lg px-3 py-2` + ` text-[${activeColor}]`;
    return `font-medium transition-colors duration-300 ${textColor} ${hoverBg} rounded-lg px-3 py-2`;
  };
  const navLinkStyle = (path) => isActive(path) ? { color: activeColor } : {};

  return (
    <motion.header
      className={`w-full z-50 fixed top-0 left-0 right-0 transition-all duration-300 ${headerBg}`}
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
            <img src="/logo.png" alt="Great Messiah Publication" className="h-10 w-10 object-contain" />
            <span className={`text-2xl font-bold tracking-tight transition-colors duration-300 ${textColor}`}>
              Great Messiah Publication
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            <DropdownMenu>
              <DropdownMenuTrigger
                className={`flex items-center gap-1 font-medium transition-colors duration-300 ${isActive("/mock-exams") ? "" : textColor} ${hoverBg} rounded-lg px-3 py-2`}
                style={navLinkStyle("/mock-exams")}
              >
                Mock Exams
                <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                <DropdownMenuItem asChild>
                  <Link to="/mock-exams">All Mock Exams</Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/mock-exams?class_level=Basic 9">Basic 9 (BECE)</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/mock-exams?class_level=Basic 8">Basic 8</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/mock-exams?class_level=Basic 7">Basic 7</Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {isSectionEnabled("for_schools") && (
            <Link
              to="/school-services"
              className={navLinkClass("/school-services")}
              style={navLinkStyle("/school-services")}
            >
              For Schools
            </Link>
            )}

            {isSectionEnabled("testimonials") && (
            <Link
              to="/success-stories"
              className={navLinkClass("/success-stories")}
              style={navLinkStyle("/success-stories")}
            >
              Success Stories
            </Link>
            )}

            {isSectionEnabled("featured_products") && (
            <Link
              to="/featured-products"
              className={navLinkClass("/featured-products")}
              style={navLinkStyle("/featured-products")}
            >
              Products
            </Link>
            )}

            {isSectionEnabled("quiz_challenge") && (
            <Link
              to="/quiz-challenge"
              className={navLinkClass("/quiz-challenge")}
              style={navLinkStyle("/quiz-challenge")}
            >
              Quiz Challenge
            </Link>
            )}
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className={`hidden sm:flex transition-colors duration-300 ${textColor} ${hoverBg}`}
              onClick={() => navigate("/mock-exams")}
              data-testid="search-btn"
            >
              <Search className="w-5 h-5" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className={`relative transition-colors duration-300 ${textColor} ${hoverBg}`}
              onClick={() => navigate("/checkout")}
              data-testid="cart-button"
            >
              <ShoppingCart className="w-5 h-5" />
              <AnimatePresence>
                {itemCount > 0 && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                    className="absolute -top-1 -right-1 w-5 h-5 bg-accent text-white text-xs font-bold rounded-full flex items-center justify-center"
                    data-testid="cart-count"
                  >
                    {itemCount}
                  </motion.span>
                )}
              </AnimatePresence>
            </Button>

            {user && <NotificationBell className={`${textColor} ${hoverBg}`} />}

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={`transition-colors duration-300 ${textColor} ${hoverBg} rounded-full overflow-hidden`}
                    data-testid="user-menu-btn"
                  >
                    <img
                      src={resolveImageUrl(user.picture) || "/default-avatar.png"}
                      alt=""
                      className="w-8 h-8 rounded-full object-cover"
                      referrerPolicy="no-referrer"
                      onError={(e) => { e.target.onerror = null; e.target.src = "/default-avatar.png"; }}
                    />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <div className="px-2 py-1.5 text-sm">
                    <p className="font-medium">Account</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard">
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      My Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/orders">
                      <History className="w-4 h-4 mr-2" />
                      Order History
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/my-devices">
                      <Monitor className="w-4 h-4 mr-2" />
                      My Devices{deviceCount != null ? ` (${deviceCount})` : ""}
                    </Link>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin">
                        <Settings className="w-4 h-4 mr-2" />
                        Admin Panel
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-red-600">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                asChild
                className={`hidden sm:inline-flex rounded-full px-6 transition-all duration-300 ${
                  isDark
                    ? "bg-gray-700 border border-gray-600 text-gray-100 hover:bg-gray-600"
                    : isHomePage && !scrolled
                      ? "bg-white/15 backdrop-blur-sm border border-white/30 text-white hover:bg-white/25"
                      : "bg-navy-900 text-white hover:bg-navy-800"
                }`}
                data-testid="sign-in-btn"
              >
                <Link to="/login">Sign In</Link>
              </Button>
            )}

            {/* Mobile Menu */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={`lg:hidden transition-colors duration-300 ${textColor} ${hoverBg}`}
                  data-testid="mobile-menu-btn"
                >
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72">
                <nav className="flex flex-col gap-4 mt-8">
                  <Link
                    to="/mock-exams"
                    className="text-lg font-medium py-2 hover:text-accent transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Mock Exams
                  </Link>
                  {isSectionEnabled("for_schools") && (
                  <Link
                    to="/school-services"
                    className="text-lg font-medium py-2 hover:text-accent transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    For Schools
                  </Link>
                  )}
                  {isSectionEnabled("testimonials") && (
                  <Link
                    to="/success-stories"
                    className="text-lg font-medium py-2 hover:text-accent transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Success Stories
                  </Link>
                  )}
                  {isSectionEnabled("featured_products") && (
                  <Link
                    to="/featured-products"
                    className="text-lg font-medium py-2 hover:text-accent transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Products
                  </Link>
                  )}
                  {isSectionEnabled("quiz_challenge") && (
                  <Link
                    to="/quiz-challenge"
                    className="text-lg font-medium py-2 hover:text-accent transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Quiz Challenge
                  </Link>
                  )}
                  {!user && (
                    <div className="pt-4 mt-4 border-t space-y-3">
                      <Button asChild className="w-full rounded-full bg-navy-900">
                        <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                          Sign In
                        </Link>
                      </Button>
                      <Button asChild variant="outline" className="w-full rounded-full">
                        <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                          Sign Up
                        </Link>
                      </Button>
                    </div>
                  )}
                  {user && (
                    <div className="pt-4 mt-4 border-t space-y-2">
                      <Link
                        to="/dashboard"
                        className="text-lg font-medium py-2 hover:text-accent transition-colors flex items-center gap-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <LayoutDashboard className="w-5 h-5" /> Dashboard
                      </Link>
                      <Link
                        to="/orders"
                        className="text-lg font-medium py-2 hover:text-accent transition-colors flex items-center gap-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <History className="w-5 h-5" /> Order History
                      </Link>
                      <Link
                        to="/my-devices"
                        className="text-lg font-medium py-2 hover:text-accent transition-colors flex items-center gap-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <Monitor className="w-5 h-5" /> My Devices{deviceCount != null ? ` (${deviceCount})` : ""}
                      </Link>
                    </div>
                  )}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </motion.header>
  );
}
